<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { reactive, defineEmits, ref, computed } from 'vue';

import CardComponent from '@/components/ui/CardComponent.vue';
import validators from '@/utils/validators.ts';

import type { ValidationRule } from '@/types/utils/validators.type.ts';
import type { AploAsset } from '@/types/services/aplo/aplo.type.ts';

const { t } = useI18n();

const emits = defineEmits(['closeModal']);

const props = defineProps<{
  availableAssets: AploAsset[],
  initial: { id: number, name: string; percentage: number } | null
}>();

const formValid = ref<boolean>(false);
const assetPercentageRules = ref<ValidationRule[]>([...validators.mandateAssetPercentageRules])

const form = reactive({
  id: null as number | null,
  name: null as string | null,
  percentage: null as number | null
});

if (props.initial) {
  form.id = props.initial.id
  form.name = props.initial.name
  form.percentage = props.initial.percentage
}

const title = computed(() =>
  props.initial ? t('components.assetComponent.titleEdit') : t('components.assetComponent.titleCreate')
)
</script>

<template>
  <v-form class="w-100 d-flex align-center">
    <CardComponent with-actions>
      <template #title>
        {{ title }}
      </template>
      <template #item>
        <v-form v-model="formValid" class="my-4">
          <v-row align="start">
            <v-col cols="6">
              <v-select
                v-model="form.name"
                :items="availableAssets"
                item-title="name"
                item-value="name"
                :label="t('components.assetComponent.inputs.name.label')"
                hide-details="auto"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="form.percentage"
                :label="t('components.assetComponent.inputs.percent.label')"
                :max="100"
                :min="1"
                :rules="assetPercentageRules"
                density="compact"
                hide-details="auto"
                suffix="%"
                type="number"
              />
            </v-col>
          </v-row>
        </v-form>
      </template>
      <template #actions>
        <div class="d-flex flex-row justify-space-between ga-4 mt-2">
          <v-btn
            variant="outlined"
            @click.prevent="emits('closeModal', false)"
          >
            {{ t('common.buttons.cancel') }}
          </v-btn>

          <v-btn
            :disabled="!formValid"
            color="primary"
            type="submit"
            variant="flat"
            @click="emits('closeModal', form)"
          >
            {{ t('common.buttons.validate') }}
          </v-btn>
        </div>
      </template>
    </CardComponent>
  </v-form>
</template>
